import { displayPhoneNumber } from 'src/lib/phone.utils'

import type {
  CarrierContact,
  CarrierDetailRecord,
  LocatedCarrier,
  NamedCarrier,
} from './carrier.types'

/**
 * A carrier is known by its DBA name in the field and by its legal name on
 * paper, and the directory is missing one or the other often enough that both
 * need a fallback.
 */
export const carrierName = (carrier: NamedCarrier) =>
  carrier.dbaName?.trim() || carrier.legalName?.trim() || 'Unnamed carrier'

export const locationLabel = (carrier: LocatedCarrier) =>
  [carrier.physicalCity, carrier.physicalState].filter(Boolean).join(', ') ||
  null

export const formatCarrierAddress = (
  carrier: Pick<
    CarrierDetailRecord,
    | 'physicalStreet'
    | 'physicalCity'
    | 'physicalState'
    | 'physicalZip'
    | 'physicalCountry'
  >
) =>
  [
    carrier.physicalStreet,
    carrier.physicalCity,
    [carrier.physicalState, carrier.physicalZip].filter(Boolean).join(' '),
    carrier.physicalCountry,
  ]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ') || null

export const contactCountLabel = (contactCount: number) =>
  contactCount === 0
    ? 'No contacts on file'
    : `${contactCount} contact${contactCount === 1 ? '' : 's'} on file`

/**
 * What to call a contact on screen.
 *
 * Every naming field is optional and most contacts are harvested from a form,
 * so a row often arrives with nothing but an address — which is still a better
 * answer than "Unnamed contact" when it is the thing a verification is sent
 * to.
 */
export const contactLabel = (
  contact: Pick<CarrierContact, 'contactName' | 'email' | 'phoneNumber'>
) =>
  contact.contactName?.trim() ||
  contact.email?.trim() ||
  displayPhoneNumber(contact.phoneNumber) ||
  'Unnamed contact'
